import { useRef, useState, useCallback, RefObject } from 'react';
import type { ClipData } from '../types';

interface StreamerOptions {
  videoRef: RefObject<HTMLVideoElement>; pipVideoRef?: RefObject<HTMLVideoElement>;
  onRequestFragment: (time: number, reason: string, isPreload: boolean, requestId: number) => void;
  onFetchBytes: (relativePath: string) => Promise<ArrayBuffer>;
  onTimeUpdate: (globalTime: number, totalDuration: number) => void;
  onPipChange: (segId: string | null, coords: { x: number; y: number; w: number } | null) => void;
  onReportTiming: (report: { requestId: number; waitMs: number; transferMs: number }) => void;
}

type Op = { kind: 'append'; offset: number; bytes: ArrayBuffer } | { kind: 'remove'; start: number; end: number };
// Per-chunk/per-seek tracing; flip on locally when debugging MSE playback.
const DEBUG_STREAMER = false;
const debugLog = (...args: unknown[]) => { if (DEBUG_STREAMER) console.log(...args); };
const PREFETCH_AHEAD = 5, RETRY_MS = 10000, MAX_ATTEMPTS = 5;

// Быстрое определение наличия аудиодорожки в fMP4 (поиск 'soun' handler box)
function hasAudioTrack(bytes: ArrayBuffer): boolean {
  const u8 = new Uint8Array(bytes);
  const limit = Math.min(u8.length - 4, 16384);
  for (let i = 0; i < limit; i++) {
    if (u8[i] === 0x73 && u8[i + 1] === 0x6f && u8[i + 2] === 0x75 && u8[i + 3] === 0x6e) {
      return true; // "soun"
    }
  }
  return false;
}

// Форматирование диапазонов буфера для наглядного лога
function formatRanges(sb?: SourceBuffer | null, v?: HTMLVideoElement | null): string {
  const b = getBufferedRanges(v || null, sb);
  if (!b || b.length === 0) return 'empty';
  const r: string[] = [];
  for (let i = 0; i < b.length; i++) {
    r.push(`[${b.start(i).toFixed(3)}s - ${b.end(i).toFixed(3)}s]`);
  }
  return r.join(', ');
}

// Безопасное чтение буферизованных диапазонов без падений при закрытии MediaSource
function getBufferedRanges(v: HTMLVideoElement | null, sb?: SourceBuffer | null): TimeRanges | null {
  if (sb) {
    try {
      return sb.buffered;
    } catch {
      // SourceBuffer отсоединён или MediaSource закрыт браузером
    }
  }
  if (v) {
    try {
      return v.buffered;
    } catch {
      return null;
    }
  }
  return null;
}

const isBuffered = (v: HTMLVideoElement, t: number, sb?: SourceBuffer | null) => {
  const b = getBufferedRanges(v, sb);
  if (!b || b.length === 0) return false;
  for (let i = 0; i < b.length; i++) if (t >= b.start(i) - 0.25 && t <= b.end(i) + 0.15) return true;
  return false;
};

const bufferedEndAt = (v: HTMLVideoElement, t: number, sb?: SourceBuffer | null) => {
  const b = getBufferedRanges(v, sb);
  if (!b || b.length === 0) return t;
  for (let i = 0; i < b.length; i++) if (t >= b.start(i) - 0.25 && t < b.end(i)) return b.end(i);
  return t;
};

function checkAndNudgeGap(v: HTMLVideoElement, sb?: SourceBuffer | null, name = 'main'): boolean {
  if (v.seeking) return false;
  const b = getBufferedRanges(v, sb);
  if (!b || b.length === 0) return false;
  const ct = v.currentTime;

  for (let i = 0; i < b.length; i++) {
    const start = b.start(i);
    if (ct < start && (start - ct) < 0.6) {
      debugLog(`[MSE-STREAMER] Leading edge bridge on ${name}: ${ct.toFixed(3)}s < range[${i}].start ${start.toFixed(3)}s -> jump to ${(start + 0.005).toFixed(3)}s`);
      v.currentTime = start + 0.005;
      return true;
    }
  }

  for (let i = 0; i < b.length - 1; i++) {
    const end = b.end(i);
    const nextStart = b.start(i + 1);
    const gap = nextStart - end;
    if (ct >= end - 0.12 && ct < nextStart && gap > 0 && gap < 0.8) {
      debugLog(`[MSE-STREAMER] Seamless bridge on ${name}: gap ${(gap * 1000).toFixed(0)}ms -> jump to ${(nextStart + 0.002).toFixed(3)}s`);
      v.currentTime = nextStart + 0.002;
      return true;
    }
  }
  return false;
}

function getSafeSeekTime(t: number, sb?: SourceBuffer | null, v?: HTMLVideoElement | null): number {
  const b = getBufferedRanges(v || null, sb);
  if (!b || b.length === 0) return t;
  for (let i = 0; i < b.length; i++) {
    const s = b.start(i), e = b.end(i);
    if (t >= s - 0.5 && t < s) return s + 0.005;
    if (t >= s && t <= e) return t;
    if (t > e && t <= e + 0.5) return Math.max(s + 0.005, e - 0.02);
  }
  let closestDist = Infinity, closestTime = t;
  for (let i = 0; i < b.length; i++) {
    const s = b.start(i), e = b.end(i);
    if (Math.abs(t - s) < closestDist) { closestDist = Math.abs(t - s); closestTime = s + 0.005; }
    if (Math.abs(t - e) < closestDist) { closestDist = Math.abs(t - e); closestTime = Math.max(s + 0.005, e - 0.02); }
  }
  return closestDist < 2.0 ? closestTime : t;
}

function parseVideoCodec(bytes: ArrayBuffer): string {
  const u8 = new Uint8Array(bytes);
  for (let i = 0; i + 8 < u8.length; i++) {
    if (u8[i] === 0x61 && u8[i + 1] === 0x76 && u8[i + 2] === 0x63 && u8[i + 3] === 0x43) {
      const h = (n: number) => n.toString(16).padStart(2, '0');
      return `avc1.${h(u8[i + 5])}${h(u8[i + 6])}${h(u8[i + 7])}`;
    }
  }
  return 'avc1.640028';
}

export function useVideoStreamer(options: StreamerOptions) {
  const { videoRef, pipVideoRef, onRequestFragment, onFetchBytes, onTimeUpdate, onPipChange, onReportTiming } = options;
  const s = useRef({
    video: null as HTMLVideoElement | null, ms: null as MediaSource | null, sb: null as SourceBuffer | null, objectUrl: null as string | null, msOpen: false,
    pipVideo: null as HTMLVideoElement | null, pipMs: null as MediaSource | null, pipSb: null as SourceBuffer | null, pipObjectUrl: null as string | null, pipMsOpen: false,
    totalDuration: 0, fragmentDuration: 10, clips: [] as ClipData[], ops: [] as Op[], pipOps: [] as Op[],
    requestId: 0, inFlight: false, wantTime: null as number | null, generation: 0,
    pending: null as { time: number; isPreload: boolean; reason: string; id: number; attempts: number } | null,
    retryTimer: null as ReturnType<typeof setTimeout> | null, timing: null as { id: number; sentAt: number } | null,
    isPlaying: false, isLoading: false, isSeeking: false,
    pendingInitBytes: null as ArrayBuffer | null,
    pendingPipInitBytes: null as ArrayBuffer | null,
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const rafSyncId = useRef<number | null>(null);
  const refreshRef = useRef<(targetTime?: number) => void>(() => {});
  const lastPipSeekAt = useRef<number>(0);
  const lastReportedPipId = useRef<string | null>(null);

  // Скрывает маску "Загрузка..." строго ПОСЛЕ того, как видеокарта отрисовала новый кадр (rVFC)
  function dismissWhenRendered() {
    const cur = s.current, v = cur.video;
    if (!v) { cur.isSeeking = false; setLoading(false); return; }
    const pv = cur.pipVideo || pipVideoRef?.current;
    const hasPip = cur.clips.some(c => c.has_overlay && (c.global_start + c.duration > v.currentTime) && c.global_start <= v.currentTime);

    let vDone = false, pvDone = !hasPip || !pv;
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      cur.isSeeking = false;
      setLoading(false);
    };

    const fallback = setTimeout(finish, 220);
    const check = () => {
      if (vDone && pvDone) {
        clearTimeout(fallback);
        finish();
      }
    };

    const anyV = v as any;
    if (typeof anyV.requestVideoFrameCallback === "function") {
      anyV.requestVideoFrameCallback(() => { vDone = true; check(); });
    } else {
      v.addEventListener("seeked", () => { vDone = true; check(); }, { once: true });
    }

    if (hasPip && pv) {
      const anyPv = pv as any;
      if (typeof anyPv.requestVideoFrameCallback === "function") {
        anyPv.requestVideoFrameCallback(() => { pvDone = true; check(); });
      } else {
        pv.addEventListener("seeked", () => { pvDone = true; check(); }, { once: true });
      }
    }
  }

  const setLoading = useCallback((v: boolean) => { s.current.isLoading = v; setIsLoading(v); }, []);
  const setPlaying = useCallback((v: boolean) => { s.current.isPlaying = v; setIsPlaying(v); }, []);

  const reqRef = useRef(onRequestFragment); reqRef.current = onRequestFragment;
  const fetchRef = useRef(onFetchBytes); fetchRef.current = onFetchBytes;
  const timeRef = useRef(onTimeUpdate); timeRef.current = onTimeUpdate;
  const pipRef = useRef(onPipChange); pipRef.current = onPipChange;
  const timingRef = useRef(onReportTiming); timingRef.current = onReportTiming;

  function dispatchOps(sb: SourceBuffer | null, queue: Op[], name = 'main') {
    if (!sb || sb.updating || !queue.length) return;
    const op = queue.shift()!;
    try {
      if (op.kind === 'append') {
        if (sb.timestampOffset !== op.offset) sb.timestampOffset = op.offset;
        sb.appendBuffer(new Uint8Array(op.bytes));
      } else {
        let hasBuffered = false;
        try { hasBuffered = sb.buffered.length > 0; } catch {}
        if (hasBuffered) {
          sb.remove(op.start, op.end);
        } else {
          dispatchOps(sb, queue, name);
        }
      }
    } catch (e: any) {
      console.error(`[MSE-STREAMER] ${name} dispatchOps error:`, e);
      if (e?.name === 'QuotaExceededError' && op.kind === 'append') {
        queue.unshift(op);
        const ct = s.current.video?.currentTime ?? 0;
        if (ct > 6 && !sb.updating) try { sb.remove(0, ct - 4); } catch {}
      } else {
        if (!sb.updating && queue.length > 0) {
          setTimeout(() => dispatchOps(sb, queue, name), 0);
        }
      }
    }
  }

  const drainAll = () => {
    dispatchOps(s.current.sb, s.current.ops, 'main');
    dispatchOps(s.current.pipSb, s.current.pipOps, 'pip');
  };

  function ensurePipPlayer() {
    const st = s.current, pv = pipVideoRef?.current;
    if (!pv || st.pipMs || !('MediaSource' in window)) return;
    try {
      const pms = new MediaSource(), purl = URL.createObjectURL(pms);
      pms.addEventListener('sourceopen', () => {
        s.current.pipMsOpen = true;
        debugLog('[MSE-STREAMER] PiP MediaSource opened');
        if (!s.current.pipSb && s.current.pendingPipInitBytes) {
          s.current.pipSb = createBuffer(pms, s.current.pendingPipInitBytes, true);
          s.current.pendingPipInitBytes = null;
        }
        drainAll();
      });
      pv.muted = true;
      pv.defaultMuted = true;
      pv.onseeked = () => {
        if (s.current.isPlaying && s.current.video && !s.current.video.paused && pv.paused) {
          pv.play().catch(() => {});
        }
      };
      pv.src = purl; st.pipVideo = pv; st.pipMs = pms; st.pipObjectUrl = purl;
    } catch (e) {
      console.error('[MSE-STREAMER] ensurePipPlayer error:', e);
    }
  }

  function maybeSetDuration() {
    const st = s.current;
    if (st.totalDuration <= 0) return;
    for (const [ms, sb] of [[st.ms, st.sb], [st.pipMs, st.pipSb]] as const) {
      if (ms && ms.readyState === 'open' && (!sb || !sb.updating)) {
        try { if (Math.abs((ms.duration || 0) - st.totalDuration) > 0.5) ms.duration = st.totalDuration; } catch {}
      }
    }
  }

  function checkPendingSeek() {
    const st = s.current;
    if (st.wantTime == null || !st.video) return;
    const t = st.wantTime;
    const buffered = isBuffered(st.video, t, st.sb);
    if (!buffered) {
      debugLog(`[STREAMER:SEEK_WAIT] Main video not buffered at ${t.toFixed(3)}s yet (main ranges: ${formatRanges(st.sb, st.video)})`);
      return;
    }

    // Основное видео спозиционировано — снимаем статус ожидания и лоадер
    st.wantTime = null;
    const targetSeek = t <= 0.001 ? 0.001 : t;
    const safeT = getSafeSeekTime(targetSeek, st.sb, st.video);
    debugLog(`[STREAMER:SEEK_RESOLVE] Target=${targetSeek.toFixed(3)}s -> SafeSeek=${safeT.toFixed(3)}s in buffer`);
    if (Math.abs(st.video.currentTime - safeT) < 0.01) {
      st.video.currentTime = safeT <= 0.005 ? 0.01 : safeT - 0.005;
    }
    st.video.currentTime = safeT;

    // Плеер камеры синхронизируем, если его буфер уже готов (не блокируя основное видео!)
    const pv = st.pipVideo || pipVideoRef?.current;
    if (pv) {
      if (isBuffered(pv, safeT, st.pipSb)) {
        const pipSafeT = getSafeSeekTime(safeT, st.pipSb, pv);
        debugLog(`[STREAMER:PIP_SEEK] PiP synced to safe=${pipSafeT.toFixed(3)}s (req=${safeT.toFixed(3)}s)`);
        pv.currentTime = pipSafeT;
        if (st.isPlaying && !st.video.paused) pv.play().catch(() => {});
      } else {
        debugLog(`[STREAMER:PIP_PENDING] PiP not buffered at ${safeT.toFixed(3)}s yet (pip ranges: ${formatRanges(st.pipSb, pv)})`);
      }
    }

    timeRef.current(safeT, st.totalDuration);
    const clip = st.clips.find(c => safeT >= c.global_start && safeT < c.global_start + c.duration);
    pipRef.current(clip?.has_overlay && clip.segment_id ? clip.segment_id : null, clip?.overlay_coords ?? null);

    dismissWhenRendered();
    if (st.isPlaying) {
      st.video.play().catch(() => {});
      if (pv && isBuffered(pv, safeT, st.pipSb)) {
        pv.play().catch(() => {});
      }
      startRaf();
    } else {
      st.video.pause();
      pv?.pause();
      stopRaf();
    }
  }

  function onBufferEnd() {
    maybeSetDuration();
    checkPendingSeek();
    maybeFill();
    drainAll();
    const st = s.current, v = st.video, pv = st.pipVideo || pipVideoRef?.current;

    if (st.sb && v) {
      checkAndNudgeGap(v, st.sb, 'main');
    }
    if (st.pipSb && pv) {
      checkAndNudgeGap(pv, st.pipSb, 'pip');
    }

    let hasBuf = false;
    try { hasBuf = Boolean(st.sb && st.sb.buffered.length > 0); } catch {}
    if (hasBuf && st.ops.length === 0 && !st.inFlight && st.wantTime == null) {
      setLoading(false);
      if (st.isPlaying && v && v.paused && isBuffered(v, v.currentTime, st.sb)) {
        v.play().catch(() => {});
      }
    }
  }

  function issueReq(time: number, isPreload: boolean, reason: string, attempts = 0) {
    const st = s.current;
    if (st.retryTimer) { clearTimeout(st.retryTimer); st.retryTimer = null; }
    st.requestId++; st.inFlight = true;
    st.pending = { time, isPreload, reason, id: st.requestId, attempts };
    st.timing = { id: st.requestId, sentAt: performance.now() };
    debugLog(`[STREAMER:REQUEST] Req #${st.requestId} (${isPreload ? "PRELOAD" : "SEEK"}): time=${time.toFixed(3)}s, reason="${reason}", curPlayhead=${st.video?.currentTime.toFixed(3)}s`);
    reqRef.current(time, reason, isPreload, st.requestId);
    st.retryTimer = setTimeout(() => {
      st.retryTimer = null;
      const p = st.pending;
      if (!p || p.id !== st.requestId || p.attempts + 1 > MAX_ATTEMPTS) {
        st.pending = null; st.inFlight = false; setLoading(false); return;
      }
      issueReq(p.time, p.isPreload, p.reason, p.attempts + 1);
    }, RETRY_MS);
  }

  function maybeFill() {
    const st = s.current, v = st.video;
    if (!v || !st.isPlaying || st.inFlight || st.totalDuration <= 0) return;
    const end = bufferedEndAt(v, v.currentTime, st.sb);
    const bufferRemaining = end - v.currentTime;
    if (end < st.totalDuration - 0.3 && bufferRemaining < PREFETCH_AHEAD) {
      debugLog(`[STREAMER:PREFETCH_TRIGGER] Playhead=${v.currentTime.toFixed(2)}s, bufferEnd=${end.toFixed(2)}s (remaining=${bufferRemaining.toFixed(2)}s < ${PREFETCH_AHEAD}s) -> Requesting preload`);
      issueReq(end, true, 'Предзагрузка');
    }
  }

  function createBuffer(ms: MediaSource | null, bytes: ArrayBuffer, isPip = false): SourceBuffer | null {
    if (!ms || ms.readyState !== 'open') return null;
    const c = parseVideoCodec(bytes);
    // Для камеры PiP поток ВСЕГДА video-only (-an). Для основного видео проверяем наличие звука.
    const hasAud = !isPip && hasAudioTrack(bytes);
    const mimes = hasAud
      ? [`video/mp4; codecs="${c}, flac"`, `video/mp4; codecs="${c}, mp4a.40.2"`, `video/mp4; codecs="${c}"`, 'video/mp4; codecs="avc1.640028, flac"', 'video/mp4; codecs="avc1.640028, mp4a.40.2"']
      : [`video/mp4; codecs="${c}"`, 'video/mp4; codecs="avc1.640028"'];
    const mime = mimes.find(m => MediaSource.isTypeSupported(m));
    debugLog(`[MSE-STREAMER] Creating ${isPip ? 'PiP' : 'Main'} SourceBuffer with MIME: ${mime} (hasAudio=${hasAud})`);
    if (!mime) return null;
    try {
      const sb = ms.addSourceBuffer(mime);
      sb.mode = 'segments';
      sb.addEventListener('updateend', onBufferEnd);
      sb.addEventListener('error', e => console.error(`[MSE-STREAMER] ${isPip ? 'PiP' : 'Main'} SourceBuffer error:`, e));
      return sb;
    } catch (e) {
      console.error(`[MSE-STREAMER] addSourceBuffer ${isPip ? 'PiP' : 'Main'} failed:`, e);
      return null;
    }
  }

  const onChunkReady = useCallback(async (data: any) => {
    const st = s.current, rAt = performance.now();
    if (data.request_id !== st.requestId) {
      debugLog(`[STREAMER:STALE_SKIP] Stale response #${data.request_id} ignored (active is #${st.requestId})`);
      return;
    }
    const gen = st.generation, sAt = st.timing?.id === data.request_id ? (st.timing?.sentAt ?? null) : null, fStart = performance.now();
    try {
      const [mb, pb] = await Promise.all([fetchRef.current(data.relative_path), data.pip_relative_path ? fetchRef.current(data.pip_relative_path) : Promise.resolve(null)]);
      if (sAt != null) timingRef.current({ requestId: data.request_id, waitMs: rAt - sAt, transferMs: performance.now() - fStart });
      if (gen !== st.generation || data.request_id !== st.requestId || !mb) {
        console.warn(`[STREAMER:STALE_DROP] Discarding stale chunk #${data.request_id} (gen ${gen} vs ${st.generation}), resetting inFlight`);
        st.inFlight = false;
        st.pending = null;
        return;
      }
      if (st.retryTimer) { clearTimeout(st.retryTimer); st.retryTimer = null; }
      st.pending = null; st.inFlight = false;

      debugLog(`[STREAMER:CHUNK_READY] Req #${data.request_id} received: start=${data.start_time?.toFixed(3)}s, req=${data.requested_time?.toFixed(3)}s, isPreload=${data.is_preload}, mainBytes=${mb.byteLength}${pb ? `, pipBytes=${pb.byteLength}, pipStart=${data.pip_start_time?.toFixed(3)}s` : ''}`);

      if (!st.sb) {
        if (st.msOpen && st.ms) st.sb = createBuffer(st.ms, mb, false);
        else st.pendingInitBytes = mb;
      }
      if (st.sb || !st.msOpen) st.ops.push({ kind: 'append', offset: data.start_time, bytes: mb });

      if (pb && data.pip_relative_path) {
        ensurePipPlayer();
        if (!st.pipSb) {
          if (st.pipMsOpen && st.pipMs) st.pipSb = createBuffer(st.pipMs, pb, true);
          else st.pendingPipInitBytes = pb;
        }
        if (st.pipSb || !st.pipMsOpen) {
          st.pipOps.push({ kind: 'append', offset: data.pip_start_time ?? data.start_time, bytes: pb });
        }
      }

      if (!data.is_preload && typeof data.requested_time === 'number') {
        st.wantTime = data.requested_time;
      }
      drainAll();
    } catch (e) {
      console.error('[STREAMER:CHUNK_ERROR] Error handling chunk:', e);
      setLoading(false);
    }
  }, []);

  const onChunkError = useCallback((data: any) => {
    const st = s.current;
    console.error(`[STREAMER:SERVER_ERROR] Server reported error for Req #${data.request_id}`);
    if (data.request_id === st.requestId) {
      if (st.retryTimer) { clearTimeout(st.retryTimer); st.retryTimer = null; }
      st.pending = null; st.inFlight = false; setLoading(false);
    }
  }, [setLoading]);

  function syncPip() {
    const st = s.current, v = st.video, pv = st.pipVideo || pipVideoRef?.current;
    if (!v || v.paused || v.seeking || !st.isPlaying) {
      if (pv && !pv.paused) pv.pause();
      return;
    }
    const t = v.currentTime;
    // Мгновенная активация PiP на 120 FPS без ожидания 250мс троттлинга timeupdate
    const curClip = st.clips.find(c => t >= c.global_start && t < c.global_start + c.duration);
    const curPipId = curClip?.has_overlay && curClip.segment_id ? curClip.segment_id : null;
    if (curPipId !== lastReportedPipId.current) {
      lastReportedPipId.current = curPipId;
      pipRef.current(curPipId, curClip?.overlay_coords ?? null);
      timeRef.current(t, st.totalDuration);
    }
    if (pv) {
      const hasPip = st.clips.some(c => c.has_overlay && (c.global_start + c.duration > t) && c.global_start <= t);
      if (!hasPip) {
        if (!pv.paused) pv.pause();
        const nextPipClip = st.clips.find(c => c.has_overlay && c.global_start > t);
        if (nextPipClip && isBuffered(pv, nextPipClip.global_start, st.pipSb)) {
          const cueT = getSafeSeekTime(nextPipClip.global_start, st.pipSb, pv);
          if (Math.abs(pv.currentTime - cueT) > 0.05 && !pv.seeking) {
            pv.currentTime = cueT;
          }
        }
      } else {
        const drift = pv.currentTime - t;
        const absDrift = Math.abs(drift);
        const now = performance.now();

        const isSeekStuck = pv.seeking && (now - lastPipSeekAt.current > 400);
        // 1. Крупный рассинхрон (> 0.6с) или зависший seek — выравниваем по буферу камеры
        if ((absDrift > 0.6 || isSeekStuck) && (now - lastPipSeekAt.current > 350)) {
          lastPipSeekAt.current = now;
          const target = getSafeSeekTime(t, st.pipSb, pv);
          debugLog(`[STREAMER:PIP_REALIGN] Realigning PiP: drift=${drift.toFixed(3)}s -> ${target.toFixed(3)}s (stuck=${isSeekStuck})`);
          pv.currentTime = target;
          if (st.isPlaying && !v.paused) pv.play().catch(() => {});
        } else if (absDrift > 0.03 && !pv.seeking) {
          // 2. Мягкая микроподстройка скорости (30-600мс)
          const rate = drift < 0 ? 1.06 : 0.94;
          if (pv.playbackRate !== rate) pv.playbackRate = rate;
        } else {
          if (pv.playbackRate !== 1.0) pv.playbackRate = 1.0;
        }

        if (st.isPlaying && !v.paused && pv.paused && isBuffered(pv, t, st.pipSb)) {
          pv.play().catch(() => {});
        }
      }
    }
    rafSyncId.current = requestAnimationFrame(syncPip);
  }

  const startRaf = () => { if (rafSyncId.current) cancelAnimationFrame(rafSyncId.current); rafSyncId.current = requestAnimationFrame(syncPip); };
  const stopRaf = () => { if (rafSyncId.current) { cancelAnimationFrame(rafSyncId.current); rafSyncId.current = null; } };

  const seek = useCallback((time: number, reason = 'Перемотка', force = false) => {
    const st = s.current, v = st.video, pv = st.pipVideo || pipVideoRef?.current;
    if (!v) return;
    time = Math.max(0, Math.min(time, Math.max(0, st.totalDuration - 0.1)));
    timeRef.current(time, st.totalDuration);

    v.pause();
    pv?.pause();
    stopRaf();

    const hasPip = st.clips.some(c => c.has_overlay && (c.global_start + c.duration > time) && c.global_start <= time);
    const targetSeek = time <= 0.001 ? 0.001 : time;

    // 1. Быстрая перемотка на месте: мгновенный перескок без маски загрузки
    if (!force && isBuffered(v, time, st.sb) && (!hasPip || (pv && st.pipSb && !st.pipSb.updating && isBuffered(pv, time, st.pipSb)))) {
      st.wantTime = targetSeek;
      if (Math.abs(v.currentTime - targetSeek) < 0.01) {
        v.currentTime = targetSeek <= 0.005 ? 0.01 : targetSeek - 0.005;
      }
      v.currentTime = targetSeek;
      if (pv && isBuffered(pv, time, st.pipSb)) pv.currentTime = targetSeek;

      const clip = st.clips.find(c => targetSeek >= c.global_start && targetSeek < c.global_start + c.duration);
      pipRef.current(clip?.has_overlay && clip.segment_id ? clip.segment_id : null, clip?.overlay_coords ?? null);

      checkPendingSeek();
      maybeFill();
      return;
    }

    // 2. Медленная перемотка через сервер
    st.isSeeking = true;
    setLoading(true);
    debugLog(`[STREAMER:SEEK_OUT_OF_BUFFER] Time=${time.toFixed(3)}s not in buffer -> fetch from server (main ranges: ${formatRanges(st.sb, v)})`);
    st.wantTime = time;
    issueReq(time, false, reason);
  }, [setLoading, pipVideoRef]);

  const togglePlay = useCallback(() => {
    const st = s.current, v = st.video, pv = st.pipVideo || pipVideoRef?.current;
    if (!v) return;
    if (st.isPlaying) {
      v.pause();
      pv?.pause();
      stopRaf();
      setPlaying(false);
      setLoading(false);
      return;
    }
    setPlaying(true);
    const rawCt = v.currentTime || 0;
    const isAtEnd = (st.totalDuration > 0 && rawCt >= st.totalDuration - 0.2);
    if (isAtEnd) {
      v.currentTime = 0.001;
      if (pv) pv.currentTime = 0.001;
    }
    const ct = isAtEnd ? 0.001 : rawCt;
    const hasPip = st.clips.some(c => c.has_overlay && (c.global_start + c.duration > ct) && c.global_start <= ct);
    if (isBuffered(v, ct, st.sb) && (!hasPip || (pv && isBuffered(pv, ct, st.pipSb)))) {
      v.play().catch(() => {});
      if (pv && isBuffered(pv, ct, st.pipSb)) pv.play().catch(() => {});
      startRaf();
      maybeFill();
      setLoading(false);
    } else {
      seek(ct, 'Нажатие Play');
    }
  }, [setPlaying, seek, pipVideoRef]);

  function onTick() {
    const st = s.current, v = st.video, pv = st.pipVideo || pipVideoRef?.current;
    if (!v) return;
    const t = v.currentTime;
    timeRef.current(t, st.totalDuration);
    const clip = st.clips.find(c => t >= c.global_start && t < c.global_start + c.duration);
    pipRef.current(clip?.has_overlay && clip.segment_id ? clip.segment_id : null, clip?.overlay_coords ?? null);
    if (st.totalDuration > 0 && t >= st.totalDuration - 0.12) {
      v.pause();
      pv?.pause();
      stopRaf();
      setPlaying(false);
      setLoading(false);
      return;
    }
    maybeFill();
  }

  const listeners = useRef<Record<string, EventListener>>({});

  const refresh = useCallback((targetTime?: number) => {
    const st = s.current, pv = st.pipVideo || pipVideoRef?.current;
    st.generation++;
    if (st.retryTimer) { clearTimeout(st.retryTimer); st.retryTimer = null; }
    st.pending = null; st.inFlight = false; st.wantTime = null; st.ops = []; st.pipOps = [];
    maybeSetDuration();
    const wasPlaying = st.isPlaying;
    st.video?.pause(); pv?.pause(); stopRaf();
    const at = typeof targetTime === 'number' ? targetTime : (st.video ? st.video.currentTime : 0);
    const end = Math.max(1, st.totalDuration || 3600);
    // Always queue the clear: if an append is still running, the old timeline lands in the
    // buffer after it. dispatchOps skips a remove on an empty buffer (see README §3).
    if (st.sb) st.ops.push({ kind: 'remove', start: 0, end });
    if (st.pipSb) st.pipOps.push({ kind: 'remove', start: 0, end });
    drainAll();
    st.wantTime = at; st.isPlaying = wasPlaying;
    setLoading(true); issueReq(at, false, 'Обновление');
  }, [setLoading, pipVideoRef]);
  refreshRef.current = refresh;

  const init = useCallback(() => {
    const st = s.current, v = videoRef.current;
    if (!v || st.ms || !('MediaSource' in window)) return;
    const ms = new MediaSource(), url = URL.createObjectURL(ms);
    ms.addEventListener('sourceopen', () => {
      s.current.msOpen = true;
      debugLog('[MSE-STREAMER] Main MediaSource opened');
      if (!s.current.sb && s.current.pendingInitBytes) {
        s.current.sb = createBuffer(ms, s.current.pendingInitBytes, false);
        s.current.pendingInitBytes = null;
      }
      maybeSetDuration();
      drainAll();
    });

    let waitingTimer: ReturnType<typeof setTimeout> | null = null;
    const clearWaitingTimer = () => {
      if (waitingTimer) { clearTimeout(waitingTimer); waitingTimer = null; }
    };

    const safeDismissLoading = () => {
      clearWaitingTimer();
      if (!s.current.inFlight && s.current.wantTime == null && !s.current.isSeeking) {
        setLoading(false);
      }
    };

    const l = {
      timeupdate: onTick as EventListener,
      play: startRaf as EventListener,
      pause: (() => {
        clearWaitingTimer();
        (s.current.pipVideo || pipVideoRef?.current)?.pause();
        stopRaf();
        safeDismissLoading();
      }) as EventListener,
      seeking: (() => {}) as EventListener,
      waiting: (() => {
        clearWaitingTimer();
        const cur = s.current;
        const curVid = cur.video;
        if (!curVid || !cur.isPlaying) return;

        const bufEnd = bufferedEndAt(curVid, curVid.currentTime, cur.sb);
        if (bufEnd - curVid.currentTime > 0.3) return;

        waitingTimer = setTimeout(() => {
          if (s.current.isPlaying && s.current.video) {
            const end = bufferedEndAt(s.current.video, s.current.video.currentTime, s.current.sb);
            if (end - s.current.video.currentTime < 0.2) {
              (s.current.pipVideo || pipVideoRef?.current)?.pause();
              setLoading(true);
            }
          }
        }, 120);
      }) as EventListener,
      playing: safeDismissLoading as EventListener,
      canplay: safeDismissLoading as EventListener,
      canplaythrough: safeDismissLoading as EventListener,
      seeked: safeDismissLoading as EventListener,
      loadeddata: safeDismissLoading as EventListener,
      error: (() => {
        const cur = s.current;
        console.error('[MSE-STREAMER] Video element error:', cur.video?.error);
        if (cur.video && cur.video.error) {
          setTimeout(() => {
            if (s.current.video) {
              refreshRef.current(s.current.video.currentTime);
            }
          }, 100);
        }
      }) as EventListener,
      ended: (() => {
        clearWaitingTimer();
        (s.current.pipVideo || pipVideoRef?.current)?.pause();
        stopRaf();
        setPlaying(false);
        setLoading(false);
      }) as EventListener,
    };
    listeners.current = l;
    Object.entries(l).forEach(([ev, fn]) => v.addEventListener(ev, fn));
    v.src = url; st.video = v; st.ms = ms; st.objectUrl = url;
    ensurePipPlayer();
  }, [videoRef, pipVideoRef, setLoading]);

  const destroy = useCallback(() => {
    const st = s.current;
    stopRaf();
    if (st.video) { Object.entries(listeners.current).forEach(([ev, fn]) => st.video?.removeEventListener(ev, fn)); listeners.current = {}; st.video.removeAttribute('src'); try { st.video.load(); } catch {} }
    if (st.pipVideo) { st.pipVideo.removeAttribute('src'); try { st.pipVideo.load(); } catch {} }
    if (st.objectUrl) URL.revokeObjectURL(st.objectUrl);
    if (st.pipObjectUrl) URL.revokeObjectURL(st.pipObjectUrl);
    if (st.retryTimer) { clearTimeout(st.retryTimer); st.retryTimer = null; }
    st.video = st.pipVideo = null; st.ms = st.pipMs = null; st.sb = st.pipSb = null;
    st.objectUrl = st.pipObjectUrl = null; st.msOpen = st.pipMsOpen = false;
    st.ops = []; st.pipOps = []; st.inFlight = false; st.isSeeking = false; st.wantTime = null; st.pending = null;
  }, []);

  return {
    init, destroy, seek, togglePlay, onChunkReady, onChunkError, refresh,
    setTotalDuration: useCallback((n: number) => { s.current.totalDuration = n; maybeSetDuration(); }, []),
    setFragmentDuration: useCallback((n: number) => { s.current.fragmentDuration = n; }, []),
    setClips: useCallback((clips: ClipData[]) => { s.current.clips = clips; }, []),
    isPlaying, isLoading,
  };
}
